/**
 * CsvExportButton — small icon button that downloads the result of an
 * admin endpoint as a CSV file. Wraps `downloadCsvFromApi` from `lib/csv`.
 *
 * Designed to slot into the corner of a ChartCard header. Disabled while
 * the download is in-flight to prevent multi-click duplicate downloads.
 */

import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { downloadCsvFromApi } from '@/lib/csv';
import { cn } from '@/lib/cn';

interface Props {
  /** API path including query string, e.g. `/v1/observe/dbview/market/export?modelKey=iphone_15`. */
  path: string;
  /** Filename for the downloaded file (without leading prefix). */
  filename?: string;
  /** Display label next to the icon — defaults to "CSV". */
  label?: string;
}

export default function CsvExportButton({ path, filename, label = 'CSV' }: Props) {
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setErr(null);
    try {
      await downloadCsvFromApi(path, filename);
    } catch (e: any) {
      setErr(e?.message ?? 'download failed');
      setTimeout(() => setErr(null), 4000);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      onClick={run}
      disabled={busy}
      className={cn(
        'inline-flex items-center gap-1 px-2 py-1 text-xs rounded',
        'text-slate-400 hover:text-white hover:bg-slate-800/70 transition',
        busy && 'opacity-60 cursor-not-allowed',
        err && 'text-rose-400',
      )}
      title={err ?? `Download CSV (${path.split('?')[0]})`}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
      {err ?? label}
    </button>
  );
}
